import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// Global z-index counter shared across all ModalShell instances
let globalZIndex = 100000;

// Minimised tray management
let minimisedTrayEl = null;
const getMinimisedTray = () => {
  if (!minimisedTrayEl) {
    minimisedTrayEl = document.createElement('div');
    minimisedTrayEl.id = 'modal-minimised-tray';
    Object.assign(minimisedTrayEl.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px',
      padding: '4px',
      zIndex: '999999',
      pointerEvents: 'none',
    });
    document.body.appendChild(minimisedTrayEl);
  }
  return minimisedTrayEl;
};

const ModalShell = ({
  isOpen,
  onClose,
  title,
  initialWidth = '90vw',
  maxWidth,
  initialHeight = '90vh',
  headerControls,
  children,
  className = '',
  bodyClassName = '',
  bodyStyle = {},
  headerStyle = {},
  titleStyle = {},
}) => {
  const [position, setPosition] = useState(null); // null = centred
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
  const [isMinimised, setIsMinimised] = useState(false);
  const [zIndex, setZIndex] = useState(() => ++globalZIndex);

  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPosition(null);
      setSize({ width: initialWidth, height: initialHeight });
      setIsMinimised(false);
      setZIndex(++globalZIndex);
    }
  }, [isOpen]);

  // Bring to front on click
  const bringToFront = useCallback(() => {
    setZIndex(++globalZIndex);
  }, []);

  // Escape key closes topmost modal
  useEffect(() => {
    if (!isOpen || isMinimised) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        // Only close if this modal is the topmost
        const allModals = document.querySelectorAll('[data-modal-shell]');
        let highestZ = 0;
        let highestEl = null;
        allModals.forEach(el => {
          const z = parseInt(el.style.zIndex, 10) || 0;
          if (z > highestZ) {
            highestZ = z;
            highestEl = el;
          }
        });
        if (highestEl === containerRef.current) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMinimised, onClose, zIndex]);

  // Drag handlers
  const handleDragStart = useCallback((e) => {
    if (e.button !== 0) return; // left click only
    e.preventDefault();
    isDragging.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    bringToFront();
  }, [bringToFront]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging.current) {
        const newLeft = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
        const newTop = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.current.y));
        setPosition({ top: newTop, left: newLeft });
      }
      if (isResizing.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newW = Math.max(300, e.clientX - rect.left);
        const newH = Math.max(200, e.clientY - rect.top);
        setSize({ width: `${newW}px`, height: `${newH}px` });
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Resize handler
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';
    bringToFront();
  }, [bringToFront]);

  if (!isOpen) return null;

  // Minimised bar — rendered into tray
  if (isMinimised) {
    return ReactDOM.createPortal(
      <div
        style={{
          background: '#072040',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          borderRadius: '6px 6px 0 0',
          cursor: 'pointer',
          fontSize: '0.85rem',
          fontWeight: 500,
          maxWidth: '240px',
          pointerEvents: 'auto',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
        }}
        onClick={() => { setIsMinimised(false); bringToFront(); }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {title || 'Modal'}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setIsMinimised(false); bringToFront(); }}
          style={{
            background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
            padding: '0 4px', fontSize: '1rem', lineHeight: 1,
          }}
          title="Restore"
        >
          <i className="bi bi-window"></i>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
          style={{
            background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
            padding: '0 4px', fontSize: '1rem', lineHeight: 1,
          }}
          title="Close"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>,
      getMinimisedTray()
    );
  }

  // Full modal
  const containerStyle = {
    position: 'fixed',
    zIndex,
    width: size.width,
    height: size.height,
    maxWidth: maxWidth || undefined,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    ...(position
      ? { top: `${position.top}px`, left: `${position.left}px` }
      : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
  };

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      data-modal-shell="true"
      className={className}
      style={containerStyle}
      onMouseDown={bringToFront}
    >
      {/* Header — drag handle */}
      <div
        onMouseDown={handleDragStart}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid #e0e0e0',
          background: '#f8f9fa',
          cursor: 'move',
          flexShrink: 0,
          userSelect: 'none',
          gap: '12px',
          ...headerStyle,
        }}
      >
        <h3 style={{
          margin: 0, fontSize: '1rem', fontWeight: 600, color: '#072040',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: 0, flex: '0 1 auto',
          ...titleStyle,
        }}>
          {title || 'Modal'}
        </h3>

        {/* Optional extra header controls (PDF nav, zoom, etc.) */}
        {headerControls && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 auto', justifyContent: 'flex-end' }}
            onMouseDown={(e) => e.stopPropagation()} // don't drag when clicking controls
          >
            {headerControls}
          </div>
        )}

        {/* Window control buttons */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setIsMinimised(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#666', padding: '4px 6px', fontSize: '1rem',
              lineHeight: 1, borderRadius: '4px', transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            title="Minimise"
          >
            <i className="bi bi-dash-lg"></i>
          </button>
          <button
            onClick={() => { if (onClose) onClose(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#666', padding: '4px 6px', fontSize: '1rem',
              lineHeight: 1, borderRadius: '4px', transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#dc3545'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#666'; }}
            title="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        className={bodyClassName}
        style={{
          flex: 1,
          overflow: 'auto',
          ...bodyStyle,
        }}
      >
        {children}
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '18px',
          height: '18px',
          cursor: 'nwse-resize',
          zIndex: 1,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" style={{ display: 'block' }}>
          <line x1="14" y1="4" x2="4" y2="14" stroke="#bbb" strokeWidth="1.5" />
          <line x1="14" y1="8" x2="8" y2="14" stroke="#bbb" strokeWidth="1.5" />
          <line x1="14" y1="12" x2="12" y2="14" stroke="#bbb" strokeWidth="1.5" />
        </svg>
      </div>
    </div>,
    document.body
  );
};

export default ModalShell;
